import { computed, ref, useHost, type ComputedRef, type ToRefs, type Ref } from "vue";
import { useSharedState } from "composables/useSharedState";
import { useCheckedFormState } from "composables/useFormAssociated";
import { createEventDispatcher } from "@/runtime/shared/events";
import type { SwitchProps } from "./switch.types";
import type { AcceptableValue } from "types/utils";
import { switchRootClasses, switchTrackClasses, switchThumbClasses } from "./switch.styles";

export interface UseSwitchOptions {
  props?: Readonly<SwitchProps>;
}

export interface UseSwitchReturn {
  // State
  state: ToRefs<SwitchProps>;

  // Refs
  buttonRef: Ref<HTMLButtonElement | null>;

  // Computed properties
  rootClasses: ComputedRef<ReturnType<typeof switchRootClasses>>;
  trackClasses: ComputedRef<ReturnType<typeof switchTrackClasses>>;
  thumbClasses: ComputedRef<ReturnType<typeof switchThumbClasses>>;
  finalColor: ComputedRef<string>;
  finalVariant: ComputedRef<string>;
  finalSize: ComputedRef<string>;
  ariaChecked: ComputedRef<boolean>;
  ariaDisabled: ComputedRef<boolean | undefined>;
  dataDisabled: ComputedRef<boolean | undefined>;
  dataState: ComputedRef<"checked" | "unchecked">;
  formValue: ComputedRef<AcceptableValue | undefined>;
  isChecked: ComputedRef<boolean>;
  isDisabled: ComputedRef<boolean>;

  // Methods
  handleClick: () => void;

  // Component wrapper
  WrapperComponent: ComputedRef<"e-card" | "div">;
}

/** Parse model-value which may be boolean or string "true"/"false" from HTML attributes. */
const parseCheckedValue = (modelValue: boolean | "true" | "false" | undefined): boolean => {
  if (modelValue === "true") return true;
  if (modelValue === "false") return false;
  return modelValue === true;
};

const computeSwitchClasses = (
  isDisabled: ComputedRef<boolean>,
  isChecked: ComputedRef<boolean>,
  finalColor: ComputedRef<string>,
  finalVariant: ComputedRef<string>,
  finalSize: ComputedRef<string>,
  state: ToRefs<SwitchProps>,
) => {
  const rootClasses = computed(() =>
    switchRootClasses({ disabled: isDisabled.value, encased: !!state.encased?.value }),
  );

  const trackClasses = computed(() =>
    switchTrackClasses({
      color: finalColor.value as NonNullable<Parameters<typeof switchTrackClasses>[0]>["color"],
      variant: finalVariant.value as NonNullable<Parameters<typeof switchTrackClasses>[0]>["variant"],
      size: finalSize.value as NonNullable<Parameters<typeof switchTrackClasses>[0]>["size"],
      disabled: isDisabled.value,
      checked: isChecked.value,
    }),
  );

  const thumbClasses = computed(() =>
    switchThumbClasses({
      color: finalColor.value as NonNullable<Parameters<typeof switchThumbClasses>[0]>["color"],
      variant: finalVariant.value as NonNullable<Parameters<typeof switchThumbClasses>[0]>["variant"],
      size: finalSize.value as NonNullable<Parameters<typeof switchThumbClasses>[0]>["size"],
      checked: isChecked.value,
      disabled: isDisabled.value,
    }),
  );

  return { rootClasses, trackClasses, thumbClasses };
};

const computeSwitchState = (state: ToRefs<SwitchProps>) => {
  const isDisabled = computed(() => state.disabled?.value ?? false);
  const isChecked = computed(() =>
    parseCheckedValue(state.modelValue?.value as boolean | "true" | "false" | undefined),
  );
  const finalColor = computed(() => state.color?.value ?? "neutral");
  const finalVariant = computed(() => state.variant?.value ?? "subtle");
  const finalSize = computed(() => state.size?.value ?? "md");
  const formValue = computed<AcceptableValue | undefined>(() => {
    if (!isChecked.value) return;
    return state.value?.value ?? "on";
  });
  return { isDisabled, isChecked, finalColor, finalVariant, finalSize, formValue };
};

export const useSwitch = (options: UseSwitchOptions = {}): UseSwitchReturn => {
  const { props } = options;
  const host = useHost();
  const buttonRef = ref<HTMLButtonElement | null>(null);

  const state = props ? useSharedState(props) : ({} as ToRefs<SwitchProps>);
  const dispatchEvent = createEventDispatcher(host);

  const { isDisabled, isChecked, finalColor, finalVariant, finalSize, formValue } = computeSwitchState(state);

  const { rootClasses, trackClasses, thumbClasses } = computeSwitchClasses(
    isDisabled,
    isChecked,
    finalColor,
    finalVariant,
    finalSize,
    state,
  );

  const { ariaChecked, ariaDisabled, dataDisabled, dataState } = useCheckedFormState(isChecked, isDisabled);

  const handleClick = () => {
    if (isDisabled.value || !host) return;
    const newValue = !isChecked.value;
    dispatchEvent("update:modelValue", newValue);
    dispatchEvent("change", newValue);
  };

  if (host) (host as HTMLElement).focus = () => buttonRef.value?.focus();

  const WrapperComponent = computed(() => (state.encased?.value ? "e-card" : "div"));

  return {
    state,
    buttonRef,
    rootClasses,
    trackClasses,
    thumbClasses,
    finalColor,
    finalVariant,
    finalSize,
    ariaChecked,
    ariaDisabled,
    dataDisabled,
    dataState,
    formValue,
    isChecked,
    isDisabled,
    handleClick,
    WrapperComponent,
  };
};
